import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { aboutInfo, faqItems } from '@/mocks/about';

export default function AboutPage() {
  return (
    <>
      <Navbar />
      <main className="pt-16 md:pt-18">
        {/* Hero */}
        <section className="relative py-16 md:py-24 bg-background-100 overflow-hidden">
          <div className="absolute -right-20 top-10 w-64 h-64 bg-primary-100/40 rounded-full blur-3xl" />
          <div className="absolute -left-20 bottom-10 w-48 h-48 bg-accent-100/30 rounded-full blur-3xl" />
          <div className="absolute left-1/3 top-1/2 w-32 h-32 bg-secondary-200/40 rounded-full blur-2xl" />
          <div className="relative max-w-7xl mx-auto px-4 md:px-6 text-center">
            <nav className="flex items-center justify-center gap-2 text-xs text-foreground-500 mb-6" aria-label="面包屑导航">
              <Link to="/" className="hover:text-primary-500 transition-colors">首页</Link>
              <i className="ri-arrow-right-s-line text-[10px]" />
              <span className="text-primary-500 font-medium">关于我们</span>
            </nav>
            <h1 className="font-heading text-3xl md:text-5xl font-bold text-foreground-950 mb-4">
              关于<span className="text-primary-500">吃瓜网站</span>
            </h1>
            <p className="text-sm md:text-base text-foreground-500 max-w-2xl mx-auto leading-relaxed">
              {aboutInfo.tagline}
            </p>
          </div>
        </section>

        {/* Brand Intro */}
        <BrandIntroSection />
        {/* Features */}
        <FeaturesSection />
        {/* Stats */}
        <StatsSection />
        {/* Timeline */}
        <TimelineSection />
        {/* Values */}
        <ValuesSection />
        {/* FAQ */}
        <FAQSection />
        {/* Contact Form */}
        <ContactSection />
        {/* Schema.org structured data */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'Organization',
              name: '吃瓜网站',
              url: 'http://chigua91.cn/',
              description: aboutInfo.description,
              contactPoint: {
                '@type': 'ContactPoint',
                email: aboutInfo.contactInfo.email,
                contactType: 'business cooperation',
              },
              address: {
                '@type': 'PostalAddress',
                addressLocality: '北京',
                addressRegion: '朝阳区',
                streetAddress: '互联网产业园A座1508室',
              },
            }),
          }}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'FAQPage',
              mainEntity: faqItems.map((faq) => ({
                '@type': 'Question',
                name: faq.question,
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: faq.answer,
                },
              })),
            }),
          }}
        />
      </main>
      <Footer />
    </>
  );
}

function BrandIntroSection() {
  return (
    <section className="py-14 md:py-18 bg-background-50">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="max-w-3xl mx-auto text-center">
          <span className="inline-block px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-semibold mb-4">
            品牌介绍
          </span>
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-6">
            我们<span className="text-primary-500">是谁</span>
          </h2>
          <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-6">
            {aboutInfo.description}
          </p>
          <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
            {aboutInfo.mission}
          </p>
        </div>
      </div>
    </section>
  );
}

function FeaturesSection() {
  return (
    <section className="py-14 md:py-18 bg-background-100">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="text-center mb-10">
          <span className="inline-block px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-semibold mb-3">
            平台优势
          </span>
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
            为什么选择<span className="text-primary-500">吃瓜网站</span>
          </h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {aboutInfo.features.map((feature) => (
            <div key={feature.title} className="bg-background-50 rounded-xl p-6">
              <span className="w-10 h-10 flex items-center justify-center rounded-xl bg-primary-100 text-primary-500 text-lg mb-4">
                <i className={feature.icon} />
              </span>
              <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2">{feature.title}</h3>
              <p className="text-xs text-foreground-500 leading-relaxed">{feature.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function StatsSection() {
  return (
    <section className="py-14 md:py-18 bg-background-950">
      <div className="max-w-7xl mx-auto px-4 md:px-6 text-center">
        <span className="inline-block px-3 py-1 rounded-full bg-background-50/10 text-background-50/80 text-xs font-semibold mb-4">
          平台数据
        </span>
        <h2 className="font-heading text-2xl md:text-3xl font-bold text-background-50 mb-10">
          用数据<span className="text-primary-400">说话</span>
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {[
            { value: aboutInfo.stats.totalArticles, label: '累计文章', unit: '篇' },
            { value: aboutInfo.stats.monthlyVisitors, label: '月活用户' },
            { value: aboutInfo.stats.dailyUpdates, label: '每日更新', unit: '条' },
            { value: aboutInfo.stats.totalComments, label: '累计评论', unit: '条' },
          ].map((stat) => (
            <div key={stat.label} className="bg-background-50/5 rounded-xl p-6">
              <p className="font-heading text-3xl md:text-4xl font-bold text-background-50 mb-1">
                {stat.value}
              </p>
              <p className="text-xs text-background-50/60">
                {stat.label}{stat.unit ? `（${stat.unit}）` : ''}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function TimelineSection() {
  return (
    <section className="py-14 md:py-18 bg-background-50">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="text-center mb-10">
          <span className="inline-block px-3 py-1 rounded-full bg-secondary-200/70 text-secondary-700 text-xs font-semibold mb-3">
            发展历程
          </span>
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
            我们的<span className="text-primary-500">故事</span>
          </h2>
        </div>
        <div className="max-w-3xl mx-auto">
          {aboutInfo.timeline.map((item, idx) => (
            <div key={item.year} className="flex gap-4">
              <div className="flex flex-col items-center shrink-0">
                <span className={`w-3 h-3 rounded-full border-2 shrink-0 ${
                  idx === 0 ? 'bg-primary-500 border-primary-500' : 'bg-background-100 border-primary-500'
                }`} />
                {idx < aboutInfo.timeline.length - 1 && (
                  <span className="w-px flex-1 bg-primary-200 mt-1" />
                )}
              </div>
              <div className={`pb-8 ${idx === aboutInfo.timeline.length - 1 ? '' : ''}`}>
                <span className="text-xs font-semibold text-primary-500 mb-1 block">{item.year}</span>
                <p className="text-sm text-foreground-700">{item.event}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ValuesSection() {
  return (
    <section className="py-14 md:py-18 bg-background-100">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="text-center mb-10">
          <span className="inline-block px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-semibold mb-3">
            核心价值观
          </span>
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
            我们<span className="text-primary-500">坚持</span>的
          </h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {aboutInfo.teamValues.map((value) => (
            <div key={value.title} className="bg-background-50 rounded-xl p-6 text-center">
              <h3 className="font-heading text-lg font-bold text-primary-500 mb-2">{value.title}</h3>
              <p className="text-xs text-foreground-500 leading-relaxed">{value.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function FAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  return (
    <section className="py-14 md:py-18 bg-background-50">
      <div className="max-w-3xl mx-auto px-4 md:px-6">
        <div className="text-center mb-10">
          <span className="inline-block px-3 py-1 rounded-full bg-secondary-200/70 text-secondary-700 text-xs font-semibold mb-3">
            常见问题
          </span>
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
            你可能<span className="text-primary-500">想知道</span>
          </h2>
        </div>
        <div className="flex flex-col gap-3">
          {faqItems.map((faq, idx) => (
            <div key={idx} className="bg-background-100 rounded-xl overflow-hidden">
              <button
                onClick={() => setOpenIndex(openIndex === idx ? null : idx)}
                className="w-full flex items-center justify-between px-5 py-4 text-left cursor-pointer"
              >
                <span className="text-sm font-medium text-foreground-900 pr-4">{faq.question}</span>
                <span className={`w-6 h-6 flex items-center justify-center shrink-0 transition-transform duration-300 text-foreground-400 ${
                  openIndex === idx ? 'rotate-180' : ''
                }`}>
                  <i className="ri-arrow-down-s-line" />
                </span>
              </button>
              {openIndex === idx && (
                <div className="px-5 pb-4">
                  <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ContactSection() {
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = (formData.get('phone_alt') as string || '').trim();
    if (honeypot) {
      setFormStatus('success');
      return;
    }

    setFormStatus('loading');
    setFormError('');

    try {
      const body = new URLSearchParams();
      for (const [key, val] of formData.entries()) {
        if (key !== 'phone_alt') {
          body.append(key, val as string);
        }
      }

      const res = await fetch('http://chigua91.cn/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });

      const text = await res.text();
      let parsed: Record<string, unknown> = {};
      try { parsed = JSON.parse(text); } catch { /* ignore */ }

      const serverMsg = (parsed?.meta as Record<string, string>)?.message || (parsed?.message as string) || text || '';

      if (!res.ok || (parsed?.code && parsed.code !== 'OK') || serverMsg.toLowerCase().includes('spam')) {
        setFormError(serverMsg || '提交失败，请稍后重试');
        setFormStatus('error');
        return;
      }

      setFormStatus('success');
      form.reset();
    } catch {
      setFormError('网络异常，请检查网络后重试');
      setFormStatus('error');
    }
  };

  return (
    <section className="py-14 md:py-18 bg-background-100">
      <div className="max-w-3xl mx-auto px-4 md:px-6">
        <div className="text-center mb-10">
          <span className="inline-block px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-semibold mb-3">
            商务合作
          </span>
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-2">
            与我们<span className="text-primary-500">合作</span>
          </h2>
          <p className="text-sm text-foreground-500">
            品牌合作、内容授权、广告投放等商务需求，欢迎联系我们
          </p>
        </div>

        <div className="bg-background-50 rounded-2xl p-6 md:p-10">
          <div className="flex flex-wrap items-center gap-6 mb-8 pb-6 border-b border-background-200">
            <div className="flex items-center gap-3">
              <span className="w-10 h-10 flex items-center justify-center rounded-xl bg-primary-100 text-primary-500">
                <i className="ri-mail-line" />
              </span>
              <div>
                <p className="text-[11px] text-foreground-400">邮箱</p>
                <p className="text-sm font-medium text-foreground-800">{aboutInfo.contactInfo.email}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span className="w-10 h-10 flex items-center justify-center rounded-xl bg-accent-100 text-accent-500">
                <i className="ri-wechat-line" />
              </span>
              <div>
                <p className="text-[11px] text-foreground-400">微信</p>
                <p className="text-sm font-medium text-foreground-800">{aboutInfo.contactInfo.wechat}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span className="w-10 h-10 flex items-center justify-center rounded-xl bg-secondary-100 text-secondary-500">
                <i className="ri-weibo-line" />
              </span>
              <div>
                <p className="text-[11px] text-foreground-400">微博</p>
                <p className="text-sm font-medium text-foreground-800">{aboutInfo.contactInfo.weibo}</p>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit} noValidate>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label htmlFor="contact_name" className="block text-xs font-medium text-foreground-700 mb-1.5">
                  姓名 <span className="text-primary-500">*</span>
                </label>
                <input
                  id="contact_name"
                  name="name"
                  type="text"
                  required
                  placeholder="请输入您的姓名"
                  className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 transition-colors"
                />
              </div>
              <div>
                <label htmlFor="contact_company" className="block text-xs font-medium text-foreground-700 mb-1.5">
                  公司名称
                </label>
                <input
                  id="contact_company"
                  name="company"
                  type="text"
                  placeholder="请输入公司名称"
                  className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 transition-colors"
                />
              </div>
              <div>
                <label htmlFor="contact_email" className="block text-xs font-medium text-foreground-700 mb-1.5">
                  邮箱 <span className="text-primary-500">*</span>
                </label>
                <input
                  id="contact_email"
                  name="email"
                  type="email"
                  required
                  placeholder="请输入您的邮箱"
                  className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 transition-colors"
                />
              </div>
              <div>
                <label htmlFor="contact_phone" className="block text-xs font-medium text-foreground-700 mb-1.5">
                  电话
                </label>
                <input
                  id="contact_phone"
                  name="phone"
                  type="tel"
                  placeholder="请输入您的电话"
                  className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 transition-colors"
                />
              </div>
              <div className="md:col-span-2">
                <label htmlFor="contact_subject" className="block text-xs font-medium text-foreground-700 mb-1.5">
                  合作意向 <span className="text-primary-500">*</span>
                </label>
                <select
                  id="contact_subject"
                  name="subject"
                  required
                  defaultValue=""
                  className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 transition-colors cursor-pointer"
                >
                  <option value="" disabled>请选择合作意向</option>
                  <option value="brand">品牌广告合作</option>
                  <option value="content">内容授权合作</option>
                  <option value="media">媒体资源互换</option>
                  <option value="investment">投资合作</option>
                  <option value="other">其他合作</option>
                </select>
              </div>
              <div className="md:col-span-2">
                <label htmlFor="contact_message" className="block text-xs font-medium text-foreground-700 mb-1.5">
                  合作说明 <span className="text-primary-500">*</span>
                </label>
                <textarea
                  id="contact_message"
                  name="message"
                  required
                  maxLength={500}
                  rows={5}
                  placeholder="请详细描述您的合作需求（不超过500字）"
                  className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 transition-colors resize-none"
                />
              </div>
            </div>

            {/* Honeypot */}
            <input
              type="text"
              name="phone_alt"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              readOnly
              style={{
                position: 'absolute',
                left: '-9999px',
                opacity: 0,
                height: 0,
                width: 0,
              }}
            />

            <button
              type="submit"
              disabled={formStatus === 'loading'}
              className="w-full py-3 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {formStatus === 'loading' ? '提交中...' : '提交合作意向'}
            </button>
          </form>

          {formStatus === 'success' && (
            <div className="mt-4 px-4 py-3 rounded-lg bg-primary-50 text-primary-700 text-sm text-center">
              <i className="ri-check-line mr-1" />
              提交成功！我们的商务团队将在3个工作日内与您联系。
            </div>
          )}
          {formStatus === 'error' && formError && (
            <div className="mt-4 px-4 py-3 rounded-lg bg-red-50 text-red-600 text-sm text-center">
              <i className="ri-error-warning-line mr-1" />
              {formError}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}